/**
 * Frontend API client for `/api/v1/`.
 *
 * Throws `ApiError` instances with the canonical error shape from CLAUDE.md
 * §7.2 — `code`, `message`, optional `field`. UI code can pattern-match on
 * `code` and surface `message` directly to users (it's already in German).
 */

export interface ApiErrorPayload {
  code: string;
  message: string;
  field?: string;
  /**
   * Endpoint-specific extras (e.g. `scanCount` on ROOM_HAS_SCANS). Carried
   * verbatim from the backend's `error` object so handlers can inspect them.
   */
  [extra: string]: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly field?: string;
  readonly status: number;
  /** Full server-supplied error object — handlers read extras like `scanCount`. */
  readonly payload: ApiErrorPayload;
  /**
   * Parsed `Retry-After` response header in milliseconds, if the server set
   * one (e.g. on 429 or 503). Supports both numeric-seconds and HTTP-date
   * forms per RFC 7231 §7.1.3. Callers like the scan-queue drain honor this
   * over their default backoff.
   */
  readonly retryAfterMs?: number;

  constructor(status: number, payload: ApiErrorPayload, retryAfterMs?: number) {
    super(payload.message);
    this.name = 'ApiError';
    this.code = payload.code;
    this.field = payload.field;
    this.status = status;
    this.payload = payload;
    this.retryAfterMs = retryAfterMs;
  }
}

/**
 * Parse an HTTP `Retry-After` header value into milliseconds. Returns
 * `undefined` if the header is absent or unparseable. Caller decides what
 * to do with `undefined` (typically: fall back to a backoff schedule).
 */
export function parseRetryAfter(
  headerValue: string | null,
  now: number = Date.now(),
): number | undefined {
  if (!headerValue) return undefined;
  const trimmed = headerValue.trim();
  if (!trimmed) return undefined;
  // Form 1: integer (or floating-point) seconds.
  const asNum = Number(trimmed);
  if (Number.isFinite(asNum) && asNum >= 0) {
    return Math.round(asNum * 1000);
  }
  // Form 2: HTTP-date (RFC 7231).
  const asDate = Date.parse(trimmed);
  if (Number.isFinite(asDate)) {
    return Math.max(0, asDate - now);
  }
  return undefined;
}

const DEFAULT_BASE = '/api/v1';

function getBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? DEFAULT_BASE;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Overrides the default base URL (mostly used in tests). */
  baseUrl?: string;
  /** Optional AbortSignal — used for client-side timeouts. */
  signal?: AbortSignal;
  /**
   * Convenience for kiosk requests: sets `Authorization: Bearer <token>`.
   * Bearer takes precedence on the backend, so this also bypasses CSRF.
   */
  bearerToken?: string;
}

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, headers, baseUrl, bearerToken, ...rest } = opts;
  const url = `${baseUrl ?? getBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;

  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  const response = await fetch(url, init);
  const text = await response.text();
  let json: unknown;
  if (text) {
    try {
      json = JSON.parse(text) as unknown;
    } catch {
      // Non-JSON body (e.g. plain text 503 from a load balancer) — leave json
      // undefined and fall through to the canonical HTTP_ERROR fallback below.
      json = undefined;
    }
  }

  if (!response.ok) {
    const payload =
      json && typeof json === 'object' && 'error' in json
        ? (json as { error: ApiErrorPayload }).error
        : { code: 'HTTP_ERROR', message: response.statusText };
    const retryAfterMs = parseRetryAfter(response.headers.get('retry-after'));
    throw new ApiError(response.status, payload, retryAfterMs);
  }

  return json as T;
}

/**
 * Variant of {@link apiFetch} for endpoints that return non-JSON payloads
 * (e.g. CSV downloads). Errors still flow through the canonical JSON
 * `{ error: { code, message } }` shape on the failure path.
 */
export async function apiFetchText(
  path: string,
  opts: RequestOptions = {},
): Promise<{ text: string; headers: Headers }> {
  const { body, headers, baseUrl, bearerToken, ...rest } = opts;
  const url = `${baseUrl ?? getBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;

  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: {
      Accept: 'text/csv, text/plain, */*',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  const response = await fetch(url, init);
  const text = await response.text();

  if (!response.ok) {
    let payload: ApiErrorPayload = {
      code: 'HTTP_ERROR',
      message: response.statusText,
    };
    if (text) {
      try {
        const json = JSON.parse(text) as { error?: ApiErrorPayload };
        if (json && json.error) payload = json.error;
      } catch {
        // Non-JSON error body — keep the canonical fallback.
      }
    }
    throw new ApiError(response.status, payload);
  }

  return { text, headers: response.headers };
}
